import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { IoCloseSharp } from 'react-icons/io5';
import { LuClipboardPaste } from "react-icons/lu";
import { appendScalerNodeToPipeline, editScalerNode } from '../../../api/api';
import { usePipeline } from '../../../context/PipelineContext';
import { validateNodeName } from '../../../name_validation';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';
import './ScalerConfigModal.css';

// Set the app element for accessibility
try {
  Modal.setAppElement('#root');
} catch (error) {
  console.warn('Could not set app element for Modal:', error);
  // This prevents the app from crashing if the root element isn't available
}

const UNSPECIFIED_COLUMN = 'Column name not specified';

const normalizeOptionalColumn = (value, sentinelLabel = null) => {
  if (value === undefined || value === null) {
    return '';
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return '';
  }
  if (sentinelLabel && trimmed.toLowerCase() === sentinelLabel.toLowerCase()) {
    return '';
  }
  return String(value);
};

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '0.375rem',
    padding: '1.5rem',
    maxWidth: '500px',
    width: '90%',
    position: 'relative',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9999, // Higher z-index to ensure it appears on top
  }
};

const ScalerConfigModal = ({ isOpen, onClose, onConfigured, isReconfiguration = false, initialData = {} }) => {
  // Simple state initialization - similar to AdderConfigModal
  const [nodeName, setNodeName] = useState(initialData.name || '');
  const [nodeNameError, setNodeNameError] = useState('');
  const [multipleFactor, setMultipleFactor] = useState(
    initialData.multipleFactor ?? ''
  );
  const [colName, setColName] = useState(normalizeOptionalColumn(initialData.colName, UNSPECIFIED_COLUMN));
  const { session } = usePipeline(); // Use the context to get the session
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Determine if we're in edit mode (has a name AND isConfigured is true but not reconfiguring)
  const isEditing = !!initialData.name && initialData.isConfigured === true && !isReconfiguration;
  
  // Function to paste from clipboard
  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setColName(text);
      }
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
    }
  };
  
  // Validate node name
  useEffect(() => {
    // Always validate the node name, even if it's empty
    const validation = validateNodeName(nodeName);
    setNodeNameError(validation.isValid ? '' : validation.errorMessage);
  }, [nodeName]);

  useEffect(() => {
    if (!isOpen) return;
    setNodeName(initialData.name || '');
    setMultipleFactor(initialData.multipleFactor ?? '');
    setColName(normalizeOptionalColumn(initialData.colName, UNSPECIFIED_COLUMN));
    setSubmitError(null);
  }, [isOpen, initialData.name, initialData.multipleFactor, initialData.colName]);

  const handleConfigure = async () => {
    try {
      setSubmitError(null);
      // Validate node name using the validation function
      const nameValidation = validateNodeName(nodeName);
      if (!nameValidation.isValid) {
        setNodeNameError(nameValidation.errorMessage);
        // No need for alert as the button will be disabled
        return;
      }

      // Make sure we have a valid multiple factor (allow 0)
      if (multipleFactor === '' || isNaN(Number(multipleFactor))) {
        // No need for alert as the button will be disabled
        return;
      }
      
      // Get the input node name and position from initialData
      const inputNodeName = initialData.inputNodeName;
      const position = initialData.position || { x: 0, y: 0 };
      const normalizedColName = normalizeOptionalColumn(colName, UNSPECIFIED_COLUMN);
      
      // Set submitting state to show loading indicator
      setIsSubmitting(true);
      
      // We're editing if isEditing is true (which means the node has a name AND is already configured)
      
      console.log("Configuration mode:", {
        isEditing,
        isReconfiguration,
        name: initialData.name,
        isConfigured: initialData.isConfigured
      });
      
      // Make the API call if we have a valid pipeline name
      if (session.pipeline_name !== "Unsaved_Pipeline") {
        try {
          if (isEditing) {
            // We're editing an existing node
            console.log(`Calling API to edit scaler node: ${initialData.name} in pipeline: ${session.pipeline_name}`);
            console.log(`New multiplication factor: ${multipleFactor}, New column name: ${normalizedColName}`);
            
            // In edit mode, we only update the multiplication factor and column name
            await editScalerNode(
              session.pipeline_name,
              initialData.name,  // Use the original name
              multipleFactor,
              normalizedColName || null
            );
            
            console.log(`Successfully edited scaler node ${initialData.name}`);
          } else if (!isReconfiguration && inputNodeName) {
            // We're creating a new node
            console.log(`Calling API to append scaler node: ${nodeName} to pipeline: ${session.pipeline_name}`);
            console.log(`Input node: ${inputNodeName}, Multiple Factor: ${multipleFactor}, Column: ${normalizedColName}, Position: (${position.x}, ${position.y})`);
            
            await appendScalerNodeToPipeline(
              session.pipeline_name,
              nodeName,
              multipleFactor,
              normalizedColName || "",
              inputNodeName,
              position.x,
              position.y
            );
            
            console.log(`Successfully added scaler node ${nodeName} to backend`);
          } else if (isReconfiguration) {
            console.log(`Skipping API call for node ${nodeName} as it's being reconfigured by parent component`);
          }
        } catch (apiError) {
          console.error(`Error calling ${isEditing ? 'editScalerNode' : 'appendScalerNodeToPipeline'}:`, apiError);
          const info = notifyError(apiError, {
            operation: isEditing ? OP.node.editScaler : OP.node.appendScaler,
            showToast: false,
          });
          setSubmitError(info);
          // Don't continue with UI updates if API call fails
          setIsSubmitting(false);
          return;
        }
      } else {
        console.log(`Skipping API call for unsaved pipeline`);
      }
      

      
      // Call the callback to update the UI
      onConfigured({
        name: isEditing ? initialData.name : nodeName, // Keep original name in edit mode
        multipleFactor: typeof multipleFactor === 'string' ? Number(multipleFactor) : multipleFactor,
        colName: normalizedColName
      });
      
      // Reset submitting state
      setIsSubmitting(false);
      
      // Note: We don't close the modal here as it will be closed by the parent component
    } catch (error) {
      console.error('Error configuring scaler node:', error);
      const info = notifyError(error, { showToast: false });
      setSubmitError(info);
      setIsSubmitting(false);
      return; // Exit early on error
    }
  };



  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel="Scaler Configuration"
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      ariaHideApp={false} // Prevents warning about appElement
    >
      <div className="scaler-modal">
        <button onClick={onClose} className="scaler-modal-close">
          <IoCloseSharp size={24} />
        </button>
        
        <h2 className="scaler-modal-title">
          {isReconfiguration ? "Reconfigure Scaler Node" : 
           isEditing ? "Edit Scaler Parameters" : 
           "Configure New Scaler Node"}
        </h2>

        {submitError && (
          <div
            role="alert"
            className="scaler-modal-error"
            style={{
              marginTop: '0.75rem',
              marginBottom: '0.75rem',
              padding: '0.75rem',
              borderRadius: '0.375rem',
              background: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              color: '#b91c1c',
              fontSize: '0.95rem',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{submitError.title}</div>
            <div>{submitError.message}</div>
          </div>
        )}
        
        <div className="scaler-modal-field">
          <label className="scaler-modal-label">
            Node Name: <span className="required">*</span>
          </label>
          {isEditing ? (
            <input
              type="text"
              value={nodeName}
              className="scaler-modal-input"
              disabled={true}
              title="Node name cannot be changed in edit mode"
            />
          ) : (
            <input
              type="text"
              value={nodeName}
              onChange={(e) => setNodeName(e.target.value)}
              onBlur={() => {
                // Validate again on blur to ensure we show errors when user leaves the field
                const validation = validateNodeName(nodeName);
                setNodeNameError(validation.isValid ? '' : validation.errorMessage);
              }}
              className={`scaler-modal-input ${nodeNameError ? 'error' : ''}`}
              placeholder="Enter node name (letters, digits, hyphens, underscores, periods)"
              aria-invalid={!!nodeNameError}
              aria-describedby={nodeNameError ? "node-name-error" : undefined}
            />
          )}
          {!isEditing && nodeNameError && (
            <div className="scaler-modal-error" id="node-name-error" role="alert">{nodeNameError}</div>
          )}
          {!isEditing && !nodeNameError && (
            <div className="scaler-modal-hint">
              Valid characters: letters, digits, hyphens (-), underscores (_), periods (.)
            </div>
          )}
        </div>
        
        <div className="scaler-modal-field">
          <label className="scaler-modal-label">
            Multiple Factor:
          </label>
          <input
            type="number"
            value={multipleFactor}
            onChange={(e) => setMultipleFactor(e.target.value)}
            className="scaler-modal-input"
            placeholder="Enter multiple factor (e.g. 2)"
            step="0.1"
          />
        </div>

        <div className="scaler-modal-field">
          <label className="scaler-modal-label">
            Column Name:
          </label>
          <div className="input-with-icon">
            <input
              type="text"
              value={colName}
              onChange={(e) => setColName(e.target.value)}
              className="scaler-modal-input"
              placeholder="Leave blank to auto-select last output column"
              title="Optional: leave blank to auto-select the last output column from the previous node"
            />
            <button 
              type="button"
              onClick={pasteFromClipboard}
              className="paste-button"
              title="Paste column name from clipboard"
            >
              <LuClipboardPaste size={18} />
            </button>
          </div>
        </div>
        
        <div className="scaler-modal-button-container">
          <button 
            className="scaler-modal-configure-button"
            onClick={handleConfigure}
            disabled={
              isSubmitting || 
              (!isEditing && (!!nodeNameError || !nodeName)) || 
              (multipleFactor === '' || isNaN(Number(multipleFactor)))
            }
            title={
              nodeNameError ? nodeNameError : 
              (!nodeName && !isEditing) ? 'Node name is required' : 
              (multipleFactor === '' || isNaN(Number(multipleFactor))) ? 'Valid multiple factor is required' : ''
            }
          >
            {isSubmitting ? 'Processing...' : 
              isReconfiguration ? 'Reconfigure' : 
              isEditing ? 'Save Changes' : 
              'Configure'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ScalerConfigModal;